import { Chip } from '@mui/material';
import DOMPurify from 'dompurify';
import type React from 'react';
import { validHTML } from 'src/components/DynamicField/helpers';
import HighlightedText from 'src/components/HighlightedText/HighlightedText';
import { CHECKBOX_FIELD_TYPE, HTML_FIELD_TYPE, MULTISELECT_FIELD_TYPE } from 'src/constants';

// Make DOMPurify available on window for debugging purposes
(window as unknown as { DOMPurify?: typeof DOMPurify }).DOMPurify = DOMPurify;

DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
  if ('target' in node) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener');
  }
});

interface FieldValueItem {
  data: string;
}

export interface DynamicFieldType {
  show?: string;
  fieldType?: string;
  field?: {
    show?: string;
  };
  value?: FieldValueItem[] | unknown;
  label?: string;
  slug?: string;
  metadataFieldId?: string;
  fieldDefId?: string;
  isEditable?: boolean;
  choices?: string[];
}

interface DynamicFieldComponentProps {
  field: DynamicFieldType;
  highlights?: (string | string[] | null)[];
}

export const DynamicFieldComponent: React.FC<DynamicFieldComponentProps> = ({ field, highlights }) => {
  if (!field?.show) {
    return <span></span>;
  }

  if (field?.fieldType === HTML_FIELD_TYPE) {
    const purifiedHTML = DOMPurify.sanitize(field?.field?.show || '');
    return (
      <span
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized with DOMPurify before rendering
        dangerouslySetInnerHTML={{
          __html: validHTML(purifiedHTML) ? purifiedHTML : '<div>Invalid HTML Provided</div>',
        }}
      />
    );
  }

  if (field?.fieldType === MULTISELECT_FIELD_TYPE || field?.fieldType === CHECKBOX_FIELD_TYPE) {
    if (field?.value && Array.isArray(field?.value)) {
      const valueItems = field.value as FieldValueItem[];
      return (
        <>
          {valueItems.map((item, key) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: value items may have duplicate data values
            <Chip key={key} label={item.data} />
          ))}
        </>
      );
    }
  }

  if (highlights) {
    return <HighlightedText highlightTexts={highlights} text={field.show} />;
  }

  return <span>{field?.show}</span>;
};
